import { useEffect, useState } from 'react';
import { C } from '@ridgewill/brand';
import {
  blockUser,
  dropConnection,
  getBlocks,
  getConnections,
  getFamily,
  unblockUser,
  type PortalConnection,
} from '../lib/api.js';
import {
  connectionDetail,
  connectionLabel,
  findConnection,
  initialOf,
  type FoundConnection,
} from '../lib/connections.js';
import { Note, Problem, button } from '../components/form.js';
import { BlockDialog, FamilyMark, Picture, RemoveDialog } from './Connections.js';
import { FAMILY_PATH } from '../lib/family.js';

/**
 * One connection, at `/connections/<id>` (RidgeWill/account#79).
 *
 * ── WHY A PAGE, WHEN THE ROW ALREADY HAS THE BUTTONS ─────────────────
 *
 * A product that means one person links here rather than into a list. The old
 * row links, `/connections#connection-<id>`, are forwarded to this page before
 * the app renders (see `connectionPageFor`), and an out-of-date one now says it
 * is out of date instead of dropping somebody at the top of a long list with no
 * hint that the person they came for is not on it.
 *
 * Remove and Block open the SAME dialogs as the list, so the warning that a
 * removal is not protection is said once, in one place, whichever way somebody
 * arrives.
 *
 * ── NOT HERE, AND WHY ────────────────────────────────────────────────
 *
 * Settings an app declares about a single connection (auth#417). auth serves
 * those only to that app's own token, never to a session, and no app declares
 * any yet, so there is nothing true to draw. account#79 records it.
 */
export function ConnectionDetail({ id }: { id: string }) {
  const [found, setFound] = useState<FoundConnection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<PortalConnection | null>(null);
  const [blocking, setBlocking] = useState<PortalConnection | null>(null);
  /**
   * The sentence after a change that takes the person off this page's lists.
   *
   * Without it a successful removal would re-resolve to "not one of your
   * connections", which is true and reads like a failure: the page would be
   * telling somebody off for the thing they just did on purpose.
   */
  const [ended, setEnded] = useState<string | null>(null);
  /** On a guardian's connection, a dependent leaves the group from here (account#117 screen 5). */
  const [guardianOfMine, setGuardianOfMine] = useState(false);

  async function load() {
    try {
      const [mine, blocked, family] = await Promise.all([
        getConnections(),
        getBlocks(),
        getFamily().catch(() => null),
      ]);
      // `?? []` for the reason Connections.tsx gives: `call<T>` casts.
      const result = findConnection(id, mine.connections ?? [], blocked.blocks ?? []);
      setFound(result);
      const other = result.kind === 'missing' ? null : result.connection.userId;
      setGuardianOfMine(
        Boolean(other) &&
          (family?.groups ?? []).some((g) => g.myRole === 'dependent' && g.guardians.some((m) => m.userId === other)),
      );
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load this connection.');
    }
  }

  useEffect(() => {
    // Once: `id` comes from the pathname, and a different person is a full page load.
    void load();
  }, []);

  /** Run one change, then reload. Refusals show the service's own sentence. */
  async function act(change: () => Promise<unknown>, failure: string, after?: string): Promise<boolean> {
    setBusy(true);
    try {
      await change();
      if (after) setEnded(after);
      else await load();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : failure);
      return false;
    } finally {
      setBusy(false);
    }
  }

  const back = (
    <a href="/connections" style={backLink}>
      &larr; All connections
    </a>
  );

  if (ended) {
    return (
      <section style={{ maxWidth: 640 }}>
        {back}
        <p role="status" style={{ margin: '1.25rem 0 0', lineHeight: 1.55 }}>
          {ended}
        </p>
      </section>
    );
  }

  if (error && !found) return <Problem>{error}</Problem>;
  if (!found) return <Note>Loading…</Note>;

  if (found.kind === 'missing') {
    return (
      <section style={{ maxWidth: 640 }}>
        {back}
        <h2 style={title}>Not one of your connections</h2>
        <Note>They may have been removed, or the link you followed is out of date.</Note>
      </section>
    );
  }

  const c = found.connection;
  const label = connectionLabel(c);
  const detail = connectionDetail(c);
  const pending = c.state === 'pending';
  const blocked = found.kind === 'blocked';

  return (
    <>
      <section style={{ maxWidth: 640 }}>
        {back}

        <div style={person}>
          <Picture image={c.person?.image ?? null} initial={initialOf(label)} size={64} />
          <div style={{ minWidth: 0 }}>
            <h2 style={{ ...title, margin: 0, overflowWrap: 'anywhere' }}>{label.title}</h2>
            {label.handle && <Note>{label.handle}</Note>}
            {c.family && <FamilyMark />}
            {/* The block list's own sentence, since this is the same fact. */}
            {blocked ? (
              <Note>You blocked them. They cannot connect with you or invite you in any Ridgewill app, and they were not told.</Note>
            ) : (
              detail && <Note>{detail}</Note>
            )}
          </div>
        </div>

        {error && !removing && !blocking && <Problem>{error}</Problem>}

        <div style={actions}>
          {blocked ? (
            c.userId && (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void act(
                    () => unblockUser(c.userId as string),
                    'That block could not be lifted.',
                    `${label.title} is unblocked. They can connect with you and invite you again.`,
                  )
                }
                style={actionButton}
              >
                {busy ? 'Unblocking…' : 'Unblock'}
              </button>
            )
          ) : pending ? (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void act(
                  () => dropConnection(c.id),
                  'That invitation could not be withdrawn.',
                  `The invitation to ${label.title} is withdrawn.`,
                )
              }
              style={actionButton}
            >
              {busy ? 'Withdrawing…' : 'Withdraw invitation'}
            </button>
          ) : (
            <>
              {c.family ? (
                <a
                  href={guardianOfMine ? `${FAMILY_PATH}#leave` : FAMILY_PATH}
                  style={{ ...actionButton, textDecoration: 'none', display: 'inline-block' }}
                >
                  {guardianOfMine ? 'Leave Family Group' : 'Family Group'}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setRemoving(c);
                  }}
                  style={actionButton}
                >
                  Remove
                </button>
              )}
              {c.userId && (
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setBlocking(c);
                  }}
                  style={actionButton}
                >
                  Block
                </button>
              )}
            </>
          )}
        </div>
      </section>

      <RemoveDialog
        target={removing}
        onClose={() => setRemoving(null)}
        onRemove={async (r) => {
          if (
            await act(
              () => dropConnection(r.id),
              'That connection could not be removed.',
              `${connectionLabel(r).title} is no longer one of your connections.`,
            )
          ) {
            setRemoving(null);
          }
        }}
        onBlockInstead={(r) => {
          setError(null);
          setRemoving(null);
          setBlocking(r);
        }}
        busy={removing ? busy : false}
        error={removing ? error : null}
      />

      <BlockDialog
        target={blocking}
        error={blocking ? error : null}
        onClose={() => setBlocking(null)}
        onBlock={async (b) => {
          // No `after`: a block keeps the person on this page, now on the block
          // list, which is the confirmation and the way back in one place.
          if (b.userId && (await act(() => blockUser(b.userId as string), 'That account could not be blocked.'))) {
            setBlocking(null);
          }
        }}
        busy={blocking ? busy : false}
      />
    </>
  );
}

const title: React.CSSProperties = { fontSize: '1.15rem', margin: '1.25rem 0 .35rem' };

const backLink: React.CSSProperties = {
  color: C.brand,
  fontSize: '.9rem',
  textDecoration: 'none',
};

const person: React.CSSProperties = {
  display: 'flex',
  gap: '1rem',
  alignItems: 'center',
  margin: '1.25rem 0 0',
};

const actions: React.CSSProperties = { display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginTop: '1.25rem' };

const actionButton: React.CSSProperties = { ...button, marginTop: 0, whiteSpace: 'nowrap' };
